"use client";

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { X, Info } from "lucide-react";
import { SecuredFooter } from "./ui/secured-footer";
import loaderVideo from "../assets/images/loader.mp4";
import loaderLogo from "../assets/images/80eight/88-footer-white.svg";

interface DeletePayIDConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
  type?: "payid" | "email" | "phone" | "wallet";
}

const DeletePayIDConfirmModal: React.FC<DeletePayIDConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
  type = "payid",
}) => {
  const getTypeLabel = () => {
    switch (type) {
      case "email":
        return "Email";
      case "phone":
        return "Phone number";
      case "wallet":
        return "Wallet address";
      default:
        return "PayID";
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && !isDeleting && onClose()}
    >
      <DialogContent className="bg-white rounded-3xl border border-[#EBE9EF] p-0 max-w-[954px] w-[95vw] overflow-hidden">
        {/* Hidden accessible title */}
        <DialogTitle className="sr-only">
          Delete {getTypeLabel()} Confirmation
        </DialogTitle>
        <DialogDescription className="sr-only">
          Confirm that you want to delete this {getTypeLabel()}
        </DialogDescription>

        {/* Loading Screen */}
        {isDeleting ? (
          <div className="relative w-full h-[600px] flex items-center justify-center">
            <video
              src={loaderVideo}
              autoPlay
              loop
              muted
              playsInline
              className="w-[500px] h-[500px] object-contain"
            />
            <img
              src={loaderLogo}
              alt="80eight"
              className="absolute z-10 w-[160px] h-auto top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </div>
        ) : (
          <>
            {/* Info Banner */}
            <div className="mx-14 mt-8">
              <div className="bg-[#EFF1FC] rounded-2xl px-6 py-4 flex items-center justify-center gap-2">
                <Info className="w-4 h-4 text-[#6375E6] flex-shrink-0" />
                <p className="text-[14px] text-[#6375E6] font-normal">
                  Once deleted you can't get back
                </p>
              </div>
            </div>

            {/* Icon and Text */}
            <div className="flex flex-col items-center justify-center py-16 px-8">
              {/* X Icon in Circle */}
              <div className="w-[104px] h-[104px] rounded-full border-[3px] border-[#CE3070] flex items-center justify-center mb-12">
                <X className="w-10 h-10 text-[#CE3070]" strokeWidth={2} />
              </div>

              {/* Title */}
              <h2 className="text-[32px] font-semibold text-[#57249C] text-center leading-[40px] max-w-[377px]">
                Are you sure you'd like to delete this {getTypeLabel()}?
              </h2>
            </div>

            {/* Buttons */}
            <div className="px-14 pb-8 flex gap-4">
              <Button
                onClick={onClose}
                className="flex-1 h-[48px] bg-white border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full hover:bg-[#F5F3F8] transition-colors"
              >
                No, go back
              </Button>
              <Button
                onClick={onConfirm}
                className="flex-1 h-[48px] rounded-full text-[14px] font-semibold text-white"
                style={{
                  background:
                    "linear-gradient(65.44deg, #57249C 10.66%, #EB3366 113.47%)",
                }}
              >
                Yes, delete address
              </Button>
            </div>

            <SecuredFooter />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeletePayIDConfirmModal;
